import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Target, Flame, Trophy, Calendar } from 'lucide-react';
import { ChallengeCard } from '../components/challenges/ChallengeCard';



export default function ChallengesPage() {
  const [challenges, setChallenges] = useState([]);
  const [streak, setStreak] = useState({ current_streak: 0, longest_streak: 0 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { api } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [challengesRes, streakRes] = await Promise.all([
        api.get('/challenges'),
        api.get('/streak'),
      ]);
      setChallenges(challengesRes.data);
      setStreak(streakRes.data);
    } catch (error) {
      console.error('Error fetching challenges:', error);
    } finally {
      setLoading(false);
    }
  };

  const weeklyChallengess = challenges.filter(c => c.period === 'weekly');
  const permanentChallenges = challenges.filter(c => c.period === 'permanent');
  const completedCount = challenges.filter(c => c.is_completed).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Target className="w-12 h-12 text-primary animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="challenges-page">
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Défis & Objectifs</h1>
            <p className="text-muted-foreground">Complète des défis pour gagner des XP</p>
          </div>
        </div>

        {/* Streak Card */}
        <Card className="mb-6 bg-gradient-to-r from-orange-500/20 to-red-500/20 border-orange-500/30">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-orange-500/30 flex items-center justify-center">
                  <Flame className="w-8 h-8 text-orange-500" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">{streak.current_streak} {streak.current_streak > 1 ? 'jours' : 'jour'}</h2>
                  <p className="text-muted-foreground">Série de connexions</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Record personnel</p>
                <p className="text-xl font-bold text-orange-500">{streak.longest_streak} {streak.longest_streak > 1 ? 'jours' : 'jour'}</p>
              </div>
            </div>
            {streak.current_streak >= 7 && (
              <Badge className="mt-4 bg-orange-500">🔥 Marin Assidu</Badge>
            )}
            {streak.current_streak >= 30 && (
              <Badge className="mt-4 ml-2 bg-orange-600">🐺 Loup de Mer</Badge>
            )}
            {streak.current_streak >= 100 && (
              <Badge className="mt-4 ml-2 bg-orange-700">🎖️ Capitaine Respecté</Badge>
            )}
          </CardContent>
        </Card>

        {/* Progress Summary */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Cette semaine</h3>
              <span className="text-primary font-bold">{completedCount}/{challenges.length} défis</span>
            </div>
            <Progress value={(completedCount / challenges.length) * 100} className="h-3" />
          </CardContent>
        </Card>

        {/* Weekly Challenges */}
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Défis Hebdomadaires
        </h2>
        <div className="grid gap-4 mb-8">
          {weeklyChallengess.map(challenge => (
            <ChallengeCard key={challenge.id} challenge={challenge} api={api} onRefresh={fetchData} />
          ))}
        </div>

        {/* Permanent Challenges */}
        {permanentChallenges.length > 0 && (
          <>
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5" />
              Défis Permanents
            </h2>
            <div className="grid gap-4">
              {permanentChallenges.map(challenge => (
                <ChallengeCard key={challenge.id} challenge={challenge} api={api} onRefresh={fetchData} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
